import { ArrowDownTrayIcon, ArrowLeftIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';
import { LibraryItemPicker } from '../../components/studio/LibraryItemPicker';
import { TokenList } from '../../components/studio/TokenList';
import { showConfirmModal } from '../../components/ui/Modal';
import { useToast } from '../../components/ui/toast';
import { ApiError, describeApiError } from '../../data/http';
import { getTemplate, readFileAsBase64 } from '../../data/templates';
import {
  listTemplateVersions,
  previewTemplateVersion,
  setCurrentTemplateVersion,
  templateVersionDownloadUrl,
  uploadTemplateVersion
} from '../../data/versions';
import type {
  ConfirmationRequired,
  LibraryItem,
  TemplateSource,
  TemplateVersionSummary,
  UploadVersionRequest,
  VersionPreview
} from '../../data/types';

const inputClass =
  'mt-1 w-full rounded-sm border border-input bg-surface px-3 py-2 text-sm outline-none transition focus:border-ring focus:ring-2 focus:ring-ring/22';

export function TemplateVersionsPage() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { templateId } = useParams();

  const [templateName, setTemplateName] = useState('');
  const [versions, setVersions] = useState<TemplateVersionSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [source, setSource] = useState<TemplateSource>('upload');
  const [file, setFile] = useState<File | null>(null);
  const [libraryItem, setLibraryItem] = useState<LibraryItem | null>(null);
  const [changeNotes, setChangeNotes] = useState('');
  const [preview, setPreview] = useState<VersionPreview | null>(null);
  const [busy, setBusy] = useState(false);

  const ready = source === 'upload' ? file !== null : libraryItem !== null;

  const load = useCallback(() => {
    if (!templateId) return Promise.resolve();
    return Promise.all([getTemplate(templateId), listTemplateVersions(templateId)])
      .then(([detail, items]) => {
        setTemplateName(detail.template.name);
        setVersions(items);
        setError(null);
      })
      .catch((err: unknown) => setError(describeApiError(err, t('errors.loadVersions'))))
      .finally(() => setLoading(false));
  }, [templateId, t]);

  useEffect(() => {
    void load();
  }, [load]);

  const chooseSource = (next: TemplateSource) => {
    setSource(next);
    // A stale diff describing a different file is worse than none.
    setPreview(null);
  };

  const chooseFile = (chosen: File | null) => {
    setFile(chosen);
    setPreview(null);
  };

  const chooseLibraryItem = (item: LibraryItem | null) => {
    setLibraryItem(item);
    setPreview(null);
  };

  const buildRequest = async (): Promise<UploadVersionRequest> =>
    source === 'library'
      ? { libraryItemId: libraryItem!.id }
      : { fileName: file!.name, fileContentBase64: await readFileAsBase64(file!) };

  const check = async () => {
    if (!templateId || !ready) return;
    setBusy(true);
    try {
      setPreview(await previewTemplateVersion(templateId, await buildRequest()));
    } catch (err: unknown) {
      toast({ title: describeApiError(err, t('errors.uploadVersion')), type: 'error' });
    } finally {
      setBusy(false);
    }
  };

  const upload = async () => {
    if (!templateId || !ready) return;
    setBusy(true);
    try {
      await performUpload(await buildRequest());
    } catch (err: unknown) {
      toast({ title: describeApiError(err, t('errors.uploadVersion')), type: 'error' });
    } finally {
      setBusy(false);
    }
  };

  /** Retries with confirmRemoval once the author accepts the confirm-removal modal. */
  const performUpload = async (base: UploadVersionRequest, confirmRemoval = false): Promise<void> => {
    try {
      const created = await uploadTemplateVersion(templateId!, { ...base, changeNotes, confirmRemoval });
      toast({
        title: created.unchanged
          ? t('studio.detail.noSharePointChanges')
          : t('studio.versions.uploaded', { version: created.version }),
        type: created.unchanged ? 'info' : 'success'
      });
      setFile(null);
      setLibraryItem(null);
      setChangeNotes('');
      setPreview(null);
      await load();
    } catch (err: unknown) {
      if (err instanceof ApiError && err.status === 409) {
        const body = err.body as ConfirmationRequired;
        const confirmed = await showConfirmModal({
          title: t('studio.versions.confirmRemovalTitle'),
          description: t('studio.versions.confirmRemovalDescription', { tokens: body.diff.removedTokens.join(', ') }),
          variant: 'danger'
        });
        if (confirmed) await performUpload(base, true);
        return;
      }
      throw err;
    }
  };

  const publish = async (version: number) => {
    if (!templateId) return;
    setBusy(true);
    try {
      await performPublish(version);
    } catch (err: unknown) {
      toast({ title: describeApiError(err, t('errors.publishVersion')), type: 'error' });
    } finally {
      setBusy(false);
    }
  };

  /** Retries with confirmRemoval once the author accepts the confirm-removal modal. */
  const performPublish = async (version: number, confirmRemoval = false): Promise<void> => {
    try {
      const result = await setCurrentTemplateVersion(templateId!, version, confirmRemoval);
      setVersions(result.items);
      toast({ title: t('studio.versions.published', { version }), type: 'success' });
    } catch (err: unknown) {
      if (err instanceof ApiError && err.status === 409) {
        const body = err.body as ConfirmationRequired;
        const confirmed = await showConfirmModal({
          title: t('studio.versions.confirmPublishTitle'),
          description: t('studio.versions.publishWarning', {
            added: body.diff.addedTokens.length,
            removed: body.diff.removedTokens.length
          }),
          variant: 'danger'
        });
        if (confirmed) await performPublish(version, true);
        return;
      }
      throw err;
    }
  };

  if (loading) return <p className="text-sm text-muted-foreground">{t('common.loading')}</p>;
  if (error) return <p className="text-sm text-destructive">{error}</p>;

  return (
    <div className="space-y-8">
      <div>
        <Link
          to={`/studio/templates/${templateId ?? ''}`}
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground transition hover:text-foreground"
        >
          <ArrowLeftIcon className="size-4" aria-hidden="true" />
          {t('studio.versions.back')}
        </Link>
        <h1 className="mt-2 text-2xl font-semibold tracking-tight text-foreground">{t('studio.versions.title')}</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {templateName} — {t('studio.versions.subtitle')}
        </p>
      </div>

      <section className="rounded-md border border-border bg-surface p-5">
        <h2 className="text-base font-semibold text-foreground">{t('studio.versions.uploadTitle')}</h2>

        <fieldset className="mt-4">
          <legend className="text-sm font-medium text-foreground">{t('studio.upload.source')}</legend>
          <div className="mt-1 flex flex-wrap gap-4">
            {(['upload', 'library'] as const).map((option) => (
              <label key={option} className="inline-flex cursor-pointer items-center gap-2 text-sm">
                <input
                  type="radio"
                  name="version-source"
                  value={option}
                  checked={source === option}
                  onChange={() => chooseSource(option)}
                  className="size-4 accent-primary"
                />
                <span className="text-foreground">{t(`studio.upload.source_${option}`)}</span>
              </label>
            ))}
          </div>
        </fieldset>

        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          {source === 'upload' ? (
            <div>
              <label htmlFor="version-file" className="text-sm font-medium text-foreground">
                {t('studio.versions.chooseFile')}
              </label>
              <input
                id="version-file"
                type="file"
                accept=".docx,.xlsx,.pptx"
                className={inputClass}
                onChange={(event) => chooseFile(event.target.files?.[0] ?? null)}
              />
            </div>
          ) : (
            <LibraryItemPicker idPrefix="version" value={libraryItem?.id ?? ''} onChange={chooseLibraryItem} libraryType="Templates" />
          )}
          <div>
            <label htmlFor="version-notes" className="text-sm font-medium text-foreground">
              {t('studio.versions.changeNotes')}
            </label>
            <input
              id="version-notes"
              type="text"
              className={inputClass}
              placeholder={t('studio.versions.changeNotesPlaceholder')}
              value={changeNotes}
              onChange={(event) => setChangeNotes(event.target.value)}
            />
          </div>
        </div>

        {preview && (
          <div className="mt-4 space-y-2 rounded-sm border border-border bg-muted/40 p-4">
            {preview.hasChanges ? (
              <>
                <TokenList label={t('studio.versions.added')} tokens={preview.addedTokens} tone="added" />
                <TokenList label={t('studio.versions.removed')} tokens={preview.removedTokens} tone="removed" />
                <TokenList label={t('studio.versions.unchanged')} tokens={preview.unchangedTokens} tone="unchanged" />
                {preview.removedTokens.length > 0 && (
                  <p className="pt-1 text-xs text-amber-700">{t('studio.versions.removalWarning')}</p>
                )}
              </>
            ) : (
              <p className="text-sm text-muted-foreground">{t('studio.versions.noChanges')}</p>
            )}
          </div>
        )}

        <div className="mt-4 flex flex-wrap gap-3">
          <button
            type="button"
            disabled={!ready || busy}
            onClick={() => void check()}
            className="inline-flex items-center rounded-sm border border-input bg-surface px-3 py-2 text-sm font-medium text-foreground transition hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50"
          >
            {busy ? t('studio.versions.checking') : t('studio.versions.checkChanges')}
          </button>
          <button
            type="button"
            disabled={!ready || busy}
            onClick={() => void upload()}
            className="inline-flex items-center rounded-sm bg-primary px-3 py-2 text-sm font-medium text-primary-foreground transition hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {busy ? t('studio.versions.uploading') : t('studio.versions.upload')}
          </button>
        </div>
      </section>

      {versions.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t('studio.versions.empty')}</p>
      ) : (
        <ul className="space-y-3">
          {versions.map((version) => (
            <li key={version.version} className="rounded-md border border-border bg-surface p-5">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="text-base font-semibold text-foreground">v{version.version}</h3>
                    {version.isCurrent && (
                      <span
                        className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-800 ring-1 ring-inset ring-emerald-200"
                        aria-label={t('studio.versions.currentLabel', { version: version.version })}
                      >
                        <CheckCircleIcon className="size-3.5" aria-hidden="true" />
                        {t('studio.versions.current')}
                      </span>
                    )}
                  </div>
                  <p className="mt-1 truncate text-sm text-muted-foreground">
                    {version.fileName} — {t('studio.versions.tokens', { count: version.tokenCount })} —{' '}
                    {t('studio.versions.uploadedBy', { name: version.uploadedBy })}
                  </p>
                  {version.changeNotes && <p className="mt-2 text-sm text-foreground">{version.changeNotes}</p>}
                </div>

                <div className="flex shrink-0 gap-2">
                  <a
                    href={templateVersionDownloadUrl(templateId ?? '', version.version)}
                    aria-label={t('studio.versions.downloadLabel', { version: version.version })}
                    className="inline-flex items-center gap-1.5 rounded-sm border border-input bg-surface px-3 py-1.5 text-sm font-medium text-foreground transition hover:bg-muted"
                  >
                    <ArrowDownTrayIcon className="size-4" aria-hidden="true" />
                    {t('studio.versions.download')}
                  </a>
                  {!version.isCurrent && (
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => void publish(version.version)}
                      className="inline-flex items-center rounded-sm bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground transition hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {busy ? t('studio.versions.publishing') : t('studio.versions.publish')}
                    </button>
                  )}
                </div>
              </div>

              {(version.addedTokens.length > 0 || version.removedTokens.length > 0) && (
                <div className="mt-3 space-y-1.5 border-t border-border pt-3">
                  <TokenList label={t('studio.versions.added')} tokens={version.addedTokens} tone="added" />
                  <TokenList label={t('studio.versions.removed')} tokens={version.removedTokens} tone="removed" />
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
